import { useCallback, useEffect, useState } from 'react';

interface UseFileUploadProps {
  onFileSelect: (file: File) => Promise<boolean>;
  onFileDelete: () => void;
  acceptedFileTypes: string;
  maxFileSize: number; // in MB
  currentFile?: File | null;
}

export const useFileUpload = ({
  onFileSelect,
  onFileDelete,
  acceptedFileTypes,
  maxFileSize,
  currentFile,
}: UseFileUploadProps) => {
  const [file, setFile] = useState<File | null>(currentFile || null);

  useEffect(() => {
    setFile(currentFile || null);
  }, [currentFile]);

  const handleFiles = useCallback(async(fileUploaded: File) => {
    if (fileUploaded.size > maxFileSize * 1024 * 1024) {
      alert(`Le fichier ne doit pas dépasser ${maxFileSize}Mo`);
      return false;
    }

    const fileExtension = fileUploaded.name.split('.').pop().toLowerCase();
    const acceptedExtensions = acceptedFileTypes.split(',').map((type) => type.trim().toLowerCase().replace('.', ''));

    if (!fileExtension || !acceptedExtensions.includes(fileExtension)) {
      alert(`Seulement les fichiers ${acceptedFileTypes} sont autorisés`);
      return false;
    }

    const uploadSuccess = await onFileSelect(fileUploaded);
    if (uploadSuccess) {
      setFile(fileUploaded);
    } else {
      setFile(null);
    }
    return uploadSuccess;
  }, [maxFileSize, acceptedFileTypes, onFileSelect]);

  const removeFile = useCallback(() => {
    setFile(null);
    onFileDelete();
  }, [onFileDelete]);

  return {
    file,
    handleFiles,
    removeFile,
  };
};
